import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BookMyOpAPI from '../services/BookMyOpapi';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Grid,
  Alert,
  InputAdornment,
  Drawer,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Close as CloseIcon, 
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

// Predefined support ticket subjects
const SUPPORT_SUBJECTS = [
  'Appointment Booking Issue',
  'Payment & Refund Issue',
  'Doctor Availability / Schedule Issue',
  'Prescription / Medical Records Issue',
  'Notification / Reminder Problem',
  'Cancellation / Reschedule Issue',
  'Other / General Query'
];

const Communication = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  // Enhanced state management
  const [tickets, setTickets] = useState({ results: [], count: 0, next: null, previous: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    receiverType: '',
    search: '',
    ordering: '-created_at'
  });
  
  // Service providers state
  const [serviceProviders, setServiceProviders] = useState([]);
  const [loadingServiceProviders, setLoadingServiceProviders] = useState(false);
  
  // Dialog states
  const [openNewTicket, setOpenNewTicket] = useState(false);
  const [openReplyDialog, setOpenReplyDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  
  // Form states
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    receiverType: 'admin',
    serviceProviderId: '',
    attachment: null,
  });
  const [selectedSubjectType, setSelectedSubjectType] = useState('');
  const [reply, setReply] = useState({
    message: '',
    attachment: null,
  });
  
  // Form validation
  const [formErrors, setFormErrors] = useState({});

  // Fetch all tickets with enhanced filtering and pagination
  const fetchTickets = async (page = 1, newFilters = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentFilters = newFilters || filters;
      const params = {
        page: page,
        pageSize: pagination.pageSize,
        ...currentFilters
      };
      
      const response = await BookMyOpAPI.getCommunications(params);
      setTickets(response);
      
      // Update pagination state
      const totalPages = Math.ceil((response.count || 0) / pagination.pageSize);
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: totalPages,
        totalCount: response.count || 0
      }));
      
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError(error.message || 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch service providers
  const fetchServiceProviders = async () => {
    try {
      setLoadingServiceProviders(true);
      const providers = await BookMyOpAPI.getServiceProviders();
      setServiceProviders(providers);
    } catch (error) {
      console.error('Error fetching service providers:', error);
    } finally {
      setLoadingServiceProviders(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchServiceProviders();
  }, []);
  
  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    fetchTickets(1, newFilters); // Reset to page 1 when filters change
  };
  
  // Handle search
  const handleSearch = (searchTerm) => {
    const newFilters = { ...filters, search: searchTerm };
    setFilters(newFilters);
    fetchTickets(1, newFilters);
  };
  
  // Handle pagination
  const handlePageChange = (event, page) => {
    fetchTickets(page);
  };
  
  // Clear all filters
  const clearFilters = () => {
    const defaultFilters = {
      status: '',
      receiverType: '',
      search: '',
      ordering: '-created_at'
    };
    setFilters(defaultFilters);
    fetchTickets(1, defaultFilters);
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.status) count++;
    if (filters.receiverType) count++;
    if (filters.search) count++;
    if (filters.ordering !== '-created_at') count++;
    return count;
  };

  // Create new ticket
  const handleCreateTicket = async () => {
    try {
      setLoading(true);
      setFormErrors({});
      
      // Validate required fields
      const errors = {};
      if (!selectedSubjectType) {
        errors.subject = 'Please select a subject';
      } else if (selectedSubjectType === 'Other / General Query' && !newTicket.subject.trim()) {
        errors.subject = 'Please enter your custom subject';
      } else if (!newTicket.subject.trim()) {
        errors.subject = 'Subject is required';
      }
      if (!newTicket.message.trim()) errors.message = 'Message is required';
      if (newTicket.receiverType === 'service_provider' && !newTicket.serviceProviderId) {
        errors.serviceProvider = 'Service provider is required';
      }
      
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
      
      await BookMyOpAPI.createCommunication(newTicket);
      setOpenNewTicket(false);
      setNewTicket({ 
        subject: '', 
        message: '', 
        receiverType: 'admin',
        serviceProviderId: '',
        attachment: null 
      });
      setSelectedSubjectType('');
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      if (error.errors) {
        setFormErrors(error.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  // Reply to ticket
  const handleReply = async () => {
    try {
      setLoading(true);
      await BookMyOpAPI.replyCommunication(selectedTicket.id, reply);
      setOpenReplyDialog(false);
      setReply({ message: '', attachment: null });
      fetchTickets();
    } catch (error) {
      console.error('Error replying to ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  // Close ticket
  const handleCloseTicket = async (ticketId) => {
    try {
      await BookMyOpAPI.closeCommunication(ticketId);
      fetchTickets();
    } catch (error) {
      console.error('Error closing ticket:', error);
    }
  };

  // Reopen ticket
  const handleReopenTicket = async (ticketId) => {
    try {
      await BookMyOpAPI.reopenCommunication(ticketId);
      fetchTickets();
    } catch (error) {
      console.error('Error reopening ticket:', error);
    }
  };

  // Handle file upload
  const handleFileChange = (event, type) => {
    const file = event.target.files[0];
    if (type === 'new') {
      setNewTicket({ ...newTicket, attachment: file });
    } else {
      setReply({ ...reply, attachment: file });
    }
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/support/${ticketId}`);
  };

  return (
    <Box sx={{ p: {xs:2,sm:2,md:6},mt:{xs:3,sm:2,md:0},bgcolor:`${theme.palette.background.paper}`,minHeight:'90vh'}}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, bgcolor: 'transparent', mb: 3 }}>
          <Typography variant="h4">Support Tickets</Typography>
        <Box sx={{ display: 'flex', gap: 1 ,flexDirection:'flex-end'}}>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenNewTicket(true)}
          >
            New Ticket
          </Button>
          <Button
            onClick={() => setOpenFilterDrawer(true)}
            startIcon={
              <Badge badgeContent={getActiveFiltersCount()} color="primary">
                <TuneIcon />
              </Badge>
            }
            sx={{
              border: `3px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              color: `${theme.palette.primary.main}`,
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              '&:hover': {
                bgcolor: theme.palette.primary.main + '10',
              }
            }}
          >
            Sort by
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Desktop Table View */}
          {!isMobile ? (
            <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden',}}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                    <TableCell sx={{ color: theme.palette.background.paper, fontWeight: 700 }}>Receiver</TableCell>
                    <TableCell sx={{ color: theme.palette.background.paper, fontWeight: 700 }}>Subject</TableCell>
                    <TableCell sx={{ color: theme.palette.background.paper, fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ color: theme.palette.background.paper, fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ color: theme.palette.background.paper, fontWeight: 700 }}>Messages</TableCell>
                    <TableCell sx={{ color: theme.palette.background.paper, fontWeight: 700 }}>Created</TableCell>
                    <TableCell sx={{ color: theme.palette.background.paper, fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.results.map((ticket) => (
                    <TableRow
                      key={ticket.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: theme.palette.action.hover,
                        }
                      }}
                      onClick={() => handleTicketClick(ticket.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ticket.receiver_name || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            maxWidth: 250,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {ticket.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column' }}>
                          <Chip
                            label={ticket.receiver_type === 'service_provider' ? 'Service Provider' : 'Admin'}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.7rem',
                              height: 22,
                              fontWeight: 500
                            }}
                          />
                          <Chip
                            label={ticket.is_sender ? 'Sent' : 'Received'}
                            size="small"
                            color={ticket.is_sender ? 'success' : 'info'}
                            sx={{ 
                              fontSize: '0.7rem',
                              height: 22,
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.status_display || ticket.status}
                          color={
                            ticket.status?.toLowerCase() === 'open' ? 'success' :
                            ticket.status?.toLowerCase() === 'closed' ? 'error' :
                            ticket.status?.toLowerCase() === 'resolved' ? 'primary' : 'info'
                          }
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge 
                          badgeContent={ticket.message_count || 0} 
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontWeight: 600
                            }
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Messages
                          </Typography>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.primary">
                          {new Date(ticket.created_at).toLocaleString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {ticket.status?.toLowerCase() === 'open' ? (
                            <>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTicket(ticket);
                                  setOpenReplyDialog(true);
                                }}
                                sx={{ 
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  minWidth: 'auto',
                                  px: 2
                                }}
                              >
                                Reply
                              </Button>
                              {ticket.is_sender && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCloseTicket(ticket.id);
                                  }}
                                  sx={{ 
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    minWidth: 'auto'
                                  }}
                                >
                                  Close
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReopenTicket(ticket.id);
                              }}
                              sx={{ 
                                textTransform: 'none',
                                fontWeight: 600,
                                minWidth: 'auto'
                              }}
                            >
                              Reopen
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* Mobile Card View */
            <List sx={{ p: 0 }}>
              {tickets.results.map((ticket) => (
                <Card 
                  key={ticket.id} 
                  sx={{ 
                    mb: 2,
                    cursor: 'pointer',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
                      transform: 'translateY(-2px)',
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                  onClick={() => handleTicketClick(ticket.id)}
                >
                  <CardContent sx={{ p: 0 }}>
                    {/* Header Section */}
                    <Box sx={{ 
                      bgcolor: theme.palette.primary.main,
                      p: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}>
                      {/* First Line: Receiver Name & Subject */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', mb: 1.5, gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 ,flexDirection:'column'}}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700,
                              color: theme.palette.background.paper,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '1.1rem'
                            }}
                          >
                            {ticket.receiver_name || 'Unknown'}
                          </Typography>
                         
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600,
                              color: theme.palette.background.default,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '1rem'
                            }}
                          >
                            {ticket.subject}
                          </Typography>
                        </Box>
                        
                        {/* Status */}
                        <Chip
                          label={ticket.status_display || ticket.status}
                          color={
                            ticket.status?.toLowerCase() === 'open' ? 'success' :
                            ticket.status?.toLowerCase() === 'closed' ? 'error' :
                            ticket.status?.toLowerCase() === 'resolved' ? 'primary' : 'info'
                          }
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>

                      {/* Second Line: Type badges */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={ticket.receiver_type === 'service_provider' ? 'Service Provider' : 'Admin'}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: 24,
                            fontWeight: 500,
                            borderColor: theme.palette.background.paper,
                            color: theme.palette.background.paper
                          }}
                        />
                        <Chip
                          label={ticket.is_sender ? 'Sent' : 'Received'}
                          size="small"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: 24,
                            bgcolor: theme.palette.background.paper + '40',
                            color: theme.palette.background.paper,
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Message Content */}
                    <Box sx={{ p: 2 }}>
                      {/* Timestamps */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                            Created
                          </Typography>
                          <Typography variant="caption" color="text.primary" sx={{ fontWeight: 500 }}>
                            {new Date(ticket.created_at).toLocaleString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                            Updated
                          </Typography>
                          <Typography variant="caption" color="text.primary" sx={{ fontWeight: 500 }}>
                            {new Date(ticket.updated_at).toLocaleString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Footer with Message Count and Actions */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${theme.palette.divider}`
                      }}>
                        {/* Message Count Badge */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Badge 
                            badgeContent={ticket.message_count || 0} 
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                fontWeight: 600
                              }
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                              Messages
                            </Typography>
                          </Badge>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {ticket.status?.toLowerCase() === 'open' ? (
                            <>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTicket(ticket);
                                  setOpenReplyDialog(true);
                                }}
                                sx={{ 
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  px: 2
                                }}
                              >
                                Reply
                              </Button>
                              {ticket.is_sender && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCloseTicket(ticket.id);
                                  }}
                                  sx={{ 
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  Close
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReopenTicket(ticket.id);
                              }}
                              sx={{ 
                                textTransform: 'none',
                                fontWeight: 600
                              }}
                            >
                              Reopen
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </List>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* No Results */}
          {tickets.results.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No tickets found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or create a new ticket
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* New Ticket Dialog */}
      <Dialog 
        open={openNewTicket} 
        onClose={() => {
          setOpenNewTicket(false);
          setNewTicket({ 
            subject: '', 
            message: '', 
            receiverType: 'admin',
            serviceProviderId: '',
            attachment: null 
          });
          setSelectedSubjectType('');
          setFormErrors({});
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Create New Ticket</DialogTitle>
        <DialogContent>
          {/* Subject Selection Dropdown */}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Subject</InputLabel>
            <Select
              value={selectedSubjectType}
              label="Subject"
              onChange={(e) => {
                const value = e.target.value;
                setSelectedSubjectType(value);
                if (value === 'Other / General Query') {
                  // Clear subject when "Other" is selected, user will enter custom
                  setNewTicket({ ...newTicket, subject: '' });
                } else {
                  // Set subject from dropdown
                  setNewTicket({ ...newTicket, subject: value });
                }
              }}
              error={!!formErrors.subject}
            >
              {SUPPORT_SUBJECTS.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
            {formErrors.subject && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {formErrors.subject}
              </Typography>
            )}
          </FormControl>

          {/* Custom Subject Field - Show when "Other / General Query" is selected */}
          {selectedSubjectType === 'Other / General Query' && (
            <TextField
              fullWidth
              label="Enter your subject"
              value={newTicket.subject}
              onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
              margin="normal"
              error={!!formErrors.subject}
              helperText={formErrors.subject || 'Please describe your issue or query'}
              required
              placeholder="e.g., Account verification issue, Billing question, etc."
            />
          )}
          
          {/* Receiver Type Selection */}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Send to</InputLabel>
            <Select
              value={newTicket.receiverType}
              label="Send to"
              onChange={(e) => setNewTicket({ 
                ...newTicket, 
                receiverType: e.target.value,
                serviceProviderId: e.target.value === 'service_provider' ? '' : newTicket.serviceProviderId
              })}
            >
              <MenuItem value="admin">Admin Support</MenuItem>
              <MenuItem value="service_provider">Service Provider</MenuItem>
            </Select>
          </FormControl>
          
          {/* Service Provider Selection */}
          {newTicket.receiverType === 'service_provider' && (
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Select Service Provider</InputLabel>
              <Select
                value={newTicket.serviceProviderId}
                label="Service Provider"
                onChange={(e) => setNewTicket({ ...newTicket, serviceProviderId: e.target.value })}
                error={!!formErrors.serviceProvider}
              >
                {loadingServiceProviders ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  serviceProviders.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.name} - {provider.location}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.serviceProvider && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {formErrors.serviceProvider}
                </Typography>
              )}
            </FormControl>
          )}
          
          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            value={newTicket.message}
            onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
            margin="normal"
            error={!!formErrors.message}
            helperText={formErrors.message}
            required
          />
          
          <input
            type="file"
            onChange={(e) => handleFileChange(e, 'new')}
            style={{ marginTop: '16px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewTicket(false)}>Cancel</Button>
          <Button onClick={handleCreateTicket} variant="contained" disabled={loading}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={openReplyDialog} onClose={() => setOpenReplyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reply to Ticket</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            value={reply.message}
            onChange={(e) => setReply({ ...reply, message: e.target.value })}
            margin="normal"
          />
          <input
            type="file"
            onChange={(e) => handleFileChange(e, 'reply')}
            style={{ marginTop: '16px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReplyDialog(false)}>Cancel</Button>
          <Button onClick={handleReply} variant="contained" disabled={loading}>
            Send Reply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={openFilterDrawer}
        onClose={() => setOpenFilterDrawer(false)}
        PaperProps={{
          sx: {
            height: '50vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Drawer Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 , color: `${theme.palette.primary.main}`}}>
              Filters
            </Typography>
            <IconButton onClick={() => setOpenFilterDrawer(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Filter Content */}
          <Grid container spacing={2}>
            {/* Status Filter */}
            <Grid item xs={12} sm={6} >
              <FormControl fullWidth >
                <InputLabel sx={{ color: `${theme.palette.primary.main}`,fontWeight: 600 }}>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  sx={{ border: `1px 0px 1px 1px solid ${theme.palette.primary.main}`}}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Receiver Type Filter */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: `${theme.palette.primary.main}`,fontWeight: 600 }}>Receiver</InputLabel>
                <Select
                  value={filters.receiverType}
                  label="Receiver"
                  sx={{ border: `1px 0px 1px 1px solid ${theme.palette.primary.main}`}}
                  onChange={(e) => handleFilterChange('receiverType', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="service_provider">Service Provider</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Ordering */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth >
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.ordering}
                  label="Sort By"
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                  sx={{ border: `1px 0px 1px 1px solid ${theme.palette.primary.main}`,color: `${theme.palette.primary.main}`,fontWeight: 600 }}
                >
                  <MenuItem value="-created_at">Newest First</MenuItem>
                  <MenuItem value="created_at">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Search */}
            {/* <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filters.search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => handleSearch('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid> */}
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => {
                clearFilters();
                setOpenFilterDrawer(false);
              }}
              fullWidth
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenFilterDrawer(false)}
              fullWidth
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Communication;
